import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from 'react-router-dom';
import { ChannelType, CustomerType, PaymentStatus } from "helpers/enums";
import moment from "moment";
import * as yup from 'yup';
import { useSnackbar } from "notistack";

interface IOrderContext {
    order: IOrder
    isLoading: boolean

    getOrder: () => void
    saveOrder: (value: Order) => void
    deleteOrder: () => Promise<void>
    downloadDocument: (id: string, documentFormat: string, fileName: string) => void
    removeSportTechProducts: () => Promise<void>
    retrySendOrder: () => Promise<void>
}


interface IOrder {
    id: string
    orderId: string
    orderStatus: string //OrderStatus --> aggiunto in ATHENAOMS-12
    erpOrderNumber: string
    channelCode: string
    channelType: ChannelType
    createdOn: moment.Moment
    orderDate: moment.Moment
    userCanEditOrder: boolean
    lengowComments: string
    customerOrderNumber: string
    shipmentCode: string
    currency: string

    canEditBillingAndCustomerName: boolean
    canEditShippingName: boolean

    containsSportTechBrands: boolean
    allSportTechBrands: boolean

    amounts: IAmount
    customer: ICustomer
    billingAddress: IBillingAddress
    shippingAddress: IAddress
    orderLines: IOrderLine[]
    activities: IActivity[]
    erpSyncErrors: IDUESyncError
    ecom: IEcomDetail
    payment: IPaymentDetail
    due: IDueDetail
    documents: IDocument[]
}

interface IAmount {
    subtotal: number
    shipping: number
    tax: number
    total: number
    vatErpCode: string
}

interface ICustomer {
    firstName: string
    lastName: string
    email: string
    creatioId: string
    customerGroups: string
}

interface IBillingAddress {
    companyName: string
    phoneNumber: string
    addressLine1: string
    addressLine2: string
    city: string
    state: string
    stateCode: string
    postalCode: string
    countryCode: string
    customerType: CustomerType
    erpCustomerCode: string
    vatNumber: string
    fiscalCode: string
    isB2B: boolean
    eInvoiceCode: string
    eInvoicePec: string
    erpAddressCode: string
    email: string
    firstName: string
    lastName: string
}

interface IAddress {
    phoneNumber: string
    addressLine1: string
    addressLine2: string
    city: string
    state: string
    stateCode: string
    postalCode: string
    countryCode: string
    firstName: string
    lastName: string
    erpAddressCode: string
    email: string
}

interface IOrderLine {
    sku: string
    shortDescription: string
    status: string
    dueOrderLineId: number
    listPrice: number
    unitPrice: number
    quantityOrdered: number
    quantityShipped: number
    quantityCanceled: number
    forcedRowClosure: boolean
    shipmentIds: string[]
}

interface IActivity {
    date: moment.Moment
    detail: string
    user: string
    source: string
}

interface IDUESyncError {
    errorType: string
    errors: string[]
    retryNr: number
    lastRetry: moment.Moment
}

interface IEcomDetail // questo box lo facciamo vedere solo per gli ordini ecom, dove nascondiamo il box 'lengow', e viceversa
{
    salesAreaCode: string
    backofficeOperatorEmail: string
    paymentType: string
    paymentStatus: string
}

interface IPaymentDetail {
    paymentTitle: string
    paymentStatus: PaymentStatus
    notes: string
}

interface IDueDetail {
    paymentTerms: string
    paymentType: string
}

interface IDocument {
    documentName: string
    fileName: string
    documentImportId: string
    documentType: DocumentType
    documentFormat: string
}

export let orderSchema = yup.object({
    id: yup.string().default(''),
    billingPostalCode: yup.string().default('').label('Postal code'),
    billingCountryCode: yup.string().required().default('').label('Country'),
    billingCity: yup.string().required().default('').label('City'),
    billingVatNumber: yup.string().default('').nullable().label('State'),
    billingFirstName: yup.string().default('').nullable().label('First name'),
    billingLastName: yup.string().default('').nullable().label('Last name'),
    billingAddressLine1: yup.string().default('').nullable().label('Address line 1'),
    billingAddressLine2: yup.string().default('').nullable().label('Address line 2'),
    billingAddressphoneNumber: yup.string().default('').nullable().label('Phone number'),

    shippingPostalCode: yup.string().default('').label('Postal code'),
    shippingCountryCode: yup.string().required().default('').label('Country'),
    shippingCity: yup.string().required().default('').label('City'),
    shippingFirstName: yup.string().default('').nullable().label('First name'),
    shippingLastName: yup.string().default('').nullable().label('Last name'),
    shippingState: yup.string().default('').nullable().label('State'),
    shippingAddressLine1: yup.string().default('').nullable().label('Address line 1'),
    shippingAddressLine2: yup.string().default('').nullable().label('Address line 2'),
    ShippingAddressPhoneNumber: yup.string().default('').nullable().label('Phone number'),
    shippingStateCode: yup.string().default('').label('State code')
        .when("shippingCountryCode", {
            is: "IT",
            then: yup.string().required(),
            otherwise: yup.string(),
        }),

    firstName: yup.string().default('').nullable().label('First name'),
    lastName: yup.string().default('').nullable().label('Last name'),
});

type Order = yup.InferType<typeof orderSchema>;


export let orderContext = React.createContext<IOrderContext>({} as IOrderContext);

let { Provider } = orderContext;

let OrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let { enqueueSnackbar } = useSnackbar();
    let navigate = useNavigate();

    let [isLoading, setLoading] = useState<boolean>(true);
    let [isDownoadingDoc, setDownoadingDoc] = useState<boolean>(false);
    let [order, setOrder] = useState<IOrder>({} as IOrder);

    useEffect(() => {
        getOrder();
    }, [id])

    let getOrder = async () => {
        try {
            let res = await axios.get('/api/Order/GetOrder', { params: { orderId: id } });

            let { item } = res.data;
            setOrder({
                ...item,
            });

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let saveOrder = async (value: Order) => {
        setLoading(true);
        try {
            await axios.post('/api/Order/SaveOrder', value);
            enqueueSnackbar("Order address saved", { variant: "success" });

            await getOrder();
            setLoading(false);
        } catch (err) {
            enqueueSnackbar("Errror when trying to save order address", { variant: "error" });
        }
    }

    let retrySendOrder = async () => {
        setLoading(true);
        try {
            await axios.post('/api/Order/RetrySendOrder', { orderId: id });
            enqueueSnackbar("Order retry saved", { variant: "success" });

            await getOrder();
            setLoading(false);
        } catch (err) {
            enqueueSnackbar("Errror when trying to save order retry", { variant: "error" });
        }
    }

    let deleteOrder = async () => {
        if (window.confirm("Do you really want to delete this order?") === false)
            return;

        try {
            setLoading(true);
            await axios.delete(`/api/Order/DeleteOrder/${id}`);
            navigate(`/orders`);

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let downloadDocument = async (id: string, documentFormat: string, fileName: string) => {

        setDownoadingDoc(true);

        try {
            let res = await axios.get('/api/Order/GetOrderDocumentUrl', { params: { documentImportId: id, documentFormat: documentFormat, fileName: fileName } });

            window.open(res.data.item);

            setDownoadingDoc(false);
        } catch (err) {
            enqueueSnackbar("Errror trying to download document", { variant: "error" });
        }
    }

    let removeSportTechProducts = async () => {
        if (window.confirm("Do you really want to delete all sport tech products from this order?") === false)
            return;

        try {
            setLoading(true);
            await axios.post('/api/Order/RemoveSportTechProducts', { orderId: id });

            await getOrder();
        } catch (err) {
            enqueueSnackbar("Errror trying to remove sport tech products", { variant: "error" });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Provider value={{
            order,
            isLoading,

            getOrder,
            saveOrder,
            deleteOrder,
            downloadDocument,
            removeSportTechProducts,
            retrySendOrder
        }}>
            {children}
        </Provider>
    );
};

export default OrderProvider;